import React, { useState, useEffect, useRef } from 'react';
import { useAlert } from '../../hooks/useAlert';
import api from '../../services/api';

/**
 * An accessible modal dialog for editing an existing meeting.
 * @param {object} props - The component's props.
 * @param {object} props.meeting - The meeting object to be edited.
 * @param {function} props.onClose - Function to call when the modal should be closed.
 * @param {function} props.onUpdate - Function to call to refresh the dashboard data.
 */
const EditMeetingModal = ({ meeting, onClose, onUpdate }) => {
    const [name, setName] = useState(meeting.name);
    const [description, setDescription] = useState(meeting.description || '');
    const [roomType, setRoomType] = useState(meeting.room_type);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { showAlert } = useAlert();
    const firstFieldRef = useRef(null);

    // Focus the first input field when the modal opens
    useEffect(() => {
        if (firstFieldRef.current) {
            firstFieldRef.current.focus();
        }
    }, []);

    // Handle closing with the Escape key
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            await api.put(`/admin/meetings/${meeting.meet_id}`, {
                name,
                description,
                room_type: roomType,
            });
            showAlert('Meeting updated successfully!', 'success');
            onUpdate(); // Refresh the data in the dashboard
            onClose(); // Close the modal
        } catch (error) {
            showAlert(error.response?.data?.message || 'Failed to update meeting.');
            setIsSubmitting(false);
        }
    };

    const styles = {
        overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
        modal: { background: '#1F2937', padding: '2rem', borderRadius: '8px', width: '90%', maxWidth: '500px', color: 'white' },
        formGroup: { margin: '1rem 0' },
        label: { display: 'block', marginBottom: '0.5rem' },
        input: { width: '100%', padding: '0.8rem', borderRadius: '5px', border: '1px solid #4B5563', background: '#374151', color: 'white', fontSize: '1rem' },
        button: { padding: '0.8rem 1.5rem', cursor: 'pointer', border: 'none', borderRadius: '5px', color: 'white', fontSize: '1rem' },
    };

    return (
        <div 
            style={styles.overlay} 
            onClick={onClose} 
            role="dialog" 
            aria-modal="true" 
            aria-labelledby="edit-modal-title"
        >
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                <h2 id="edit-modal-title">Edit Meeting: {meeting.name}</h2>
                <form onSubmit={handleUpdate}>
                    <div style={styles.formGroup}>
                        <label htmlFor="edit-meeting-name" style={styles.label}>Meeting Name (Required)</label>
                        <input 
                            id="edit-meeting-name"
                            type="text" 
                            value={name} 
                            onChange={e => setName(e.target.value)} 
                            ref={firstFieldRef}
                            style={styles.input}
                            required 
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label htmlFor="edit-meeting-description" style={styles.label}>Description (Optional)</label>
                        <textarea 
                            id="edit-meeting-description"
                            value={description} 
                            onChange={e => setDescription(e.target.value)} 
                            style={{ ...styles.input, height: '80px' }}
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label htmlFor="edit-room-type" style={styles.label}>Room Type</label>
                        <select 
                            id="edit-room-type"
                            value={roomType} 
                            onChange={e => setRoomType(e.target.value)} 
                            style={styles.input}
                        >
                            <option value="private">Private</option>
                            <option value="public">Public</option>
                            <option value="open">Open (for Guests)</option>
                        </select>
                    </div>
                    <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: '#4B5563' }}>Cancel</button>
                        <button type="submit" disabled={isSubmitting} style={{ ...styles.button, background: '#059669' }}>
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditMeetingModal;
